/* eslint-disable no-restricted-globals */
/* eslint-disable no-nested-ternary */
import * as React from 'react';
import { useLocation, Link as RouterLink } from 'react-router-dom';
import {
  AppBar,
  Toolbar,
  Box,
  Typography,
  IconButton,
  Tooltip,
  Container,
  Avatar,
  Drawer,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import HomeIcon from '@mui/icons-material/Home';
import MenuIcon from '@mui/icons-material/Menu';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import SettingsIcon from '@mui/icons-material/Settings';
import LoginIcon from '@mui/icons-material/Login';
import LogoutIcon from '@mui/icons-material/Logout';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import DashboardIcon from '@mui/icons-material/Dashboard';
import StoreIcon from '@mui/icons-material/Store';
import CloseIcon from '@mui/icons-material/Close';

import { useAppDispatch, useAppSelector } from 'renderer/shared/state/hooks';
import {
  selectIsLoggedIn,
  selectUser,
} from 'renderer/features/auth/state/userSelectors';
import { logout } from 'renderer/features/auth/state/userSlice';
import { useGetCartQuery } from 'renderer/features/payments-page/services/cart.endpoints';

import DAlogo from './da-logo.png';
import DownloadsNav from './DownloadsNav';

const iconStyle = {
  backgroundColor: 'white',
  border: '2px solid #252525',
  color: '#252525',
  width: 40,
  height: 40,
  marginBottom: '4px',
  position: 'relative' as const,
};

const labelStyle = {
  fontSize: '0.8rem',
  color: '#252525',
  fontWeight: 600,
};

const TopBar = () => {
  const isLoggedIn = useAppSelector(selectIsLoggedIn);
  const user = useAppSelector(selectUser);
  const dispatch = useAppDispatch();
  const location = useLocation();

  const { data: cart } = useGetCartQuery(null);
  const cartItemCount =
    cart?.items?.reduce(
      (total: number, item: { quantity?: number }) =>
        total + (item.quantity || 0),
      0,
    ) || 0;

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [mobileOpen, setMobileOpen] = React.useState(false);
  const toggleDrawer = () => setMobileOpen((s) => !s);

  const handleLogout = () => {
    dispatch(logout());
  };

  return (
    <AppBar
      sx={{
        background: 'white',
        boxShadow: 'none',
        borderBottom: '1px solid #e0e0e0',
      }}
      position="static"
    >
      <Container maxWidth="lg">
        <Toolbar
          sx={{
            justifyContent: 'space-between',
            minHeight: { xs: 56, sm: 64 },
            px: 0,
          }}
        >
          {/* Logo (left) */}
          {/* <Box
            component={RouterLink}
            to="https://www.digitalanarchy.com"
            sx={{ display: 'flex', alignItems: 'center' }}
          >
            <img
              src={DAlogo}
              alt="Digital Anarchy"
              style={{ width: isMobile ? 120 : 150 }}
            />
          </Box> */}
          {/* Logo + Store CTA (left) */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            {/* Logo */}
            <Box
              component="a"
              href="https://www.digitalanarchy.com"
              target="_blank"
              rel="noopener noreferrer"
              sx={{ display: 'flex', alignItems: 'center' }}
            >
              <img
                src={DAlogo}
                alt="Digital Anarchy"
                style={{ width: isMobile ? 120 : 150 }}
              />
            </Box>

            {/* Store Button */}
            <Box
              component={RouterLink}
              to="/"
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 0.75,
                px: 1.5,
                py: 0.75,
                borderRadius: '6px',
                backgroundColor: '#E53935',
                color: 'white',
                fontWeight: 700,
                fontSize: '0.85rem',
                textDecoration: 'none',
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
                '&:hover': {
                  backgroundColor: '#d32f2f',
                },
              }}
            >
              <HomeIcon sx={{ fontSize: 18 }} />
              Store
            </Box>
          </Box>

          {/* Right side: desktop icons OR mobile hamburger */}
          {isMobile ? (
            <IconButton
              onClick={toggleDrawer}
              aria-label="open menu"
              edge="end"
              sx={{
                color: '#252525',
                border: '1px solid #dcdcdc',
                borderRadius: '8px',
              }}
            >
              <MenuIcon />
            </IconButton>
          ) : (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              {isLoggedIn && (
                <Tooltip title="Dashboard">
                  <Box
                    component={RouterLink}
                    to="/dashboard"
                    sx={{ textAlign: 'center', textDecoration: 'none' }}
                  >
                    <Avatar sx={iconStyle}>
                      <DashboardIcon fontSize="small" />
                    </Avatar>
                    <Typography sx={labelStyle}>Dashboard</Typography>
                  </Box>
                </Tooltip>
              )}

              {/* <Tooltip title="Store">
                <Box
                  component={RouterLink}
                  to="/"
                  sx={{ textAlign: 'center', textDecoration: 'none' }}
                >
                  <Avatar sx={iconStyle}>
                    <StoreIcon fontSize="small" />
                  </Avatar>
                  <Typography sx={labelStyle}>Store</Typography>
                </Box>
              </Tooltip> */}

              {isLoggedIn && (
                <Tooltip title="Settings">
                  <Box
                    component={RouterLink}
                    to="/settings"
                    sx={{ textAlign: 'center', textDecoration: 'none' }}
                  >
                    <Avatar sx={iconStyle}>
                      <SettingsIcon fontSize="small" />
                    </Avatar>
                    <Typography sx={labelStyle}>Settings</Typography>
                  </Box>
                </Tooltip>
              )}

              {!isLoggedIn ? (
                <>
                  <Tooltip title="Sign Up">
                    <Box
                      component={RouterLink}
                      to="/register"
                      sx={{ textAlign: 'center', textDecoration: 'none' }}
                    >
                      <Avatar sx={iconStyle}>
                        <PersonAddIcon fontSize="small" />
                      </Avatar>
                      <Typography sx={labelStyle}>Sign Up</Typography>
                    </Box>
                  </Tooltip>

                  <Tooltip title="Login">
                    <Box
                      component={RouterLink}
                      to="/login"
                      sx={{ textAlign: 'center', textDecoration: 'none' }}
                    >
                      <Avatar sx={iconStyle}>
                        <LoginIcon fontSize="small" />
                      </Avatar>
                      <Typography sx={labelStyle}>Login</Typography>
                    </Box>
                  </Tooltip>
                </>
              ) : (
                <Tooltip title="Logout">
                  <Box
                    sx={{
                      textAlign: 'center',
                      textDecoration: 'none',
                      cursor: 'pointer',
                    }}
                    onClick={handleLogout}
                  >
                    <Avatar sx={iconStyle}>
                      <LogoutIcon fontSize="small" />
                    </Avatar>
                    <Typography sx={labelStyle}>Logout</Typography>
                  </Box>
                </Tooltip>
              )}

              <Tooltip title="Cart">
                <Box
                  component={RouterLink}
                  to="/cart/1"
                  sx={{
                    textAlign: 'center',
                    textDecoration: 'none',
                    position: 'relative',
                    pr: 1,
                  }}
                >
                  <Avatar sx={iconStyle}>
                    <ShoppingCartIcon fontSize="small" />
                  </Avatar>

                  {cartItemCount > 0 && (
                    <Box
                      sx={{
                        position: 'absolute',
                        top: 2,
                        right: 12,
                        transform: 'translate(50%, -50%)',
                        width: cartItemCount > 99 ? 27 : 24.5,
                        height: cartItemCount > 99 ? 27 : 24.5,
                        border: '2px solid white',
                        borderRadius: '50%',
                        backgroundColor: '#45A9E0',
                        color: 'white',
                        fontSize: '0.75rem',
                        fontWeight: 700,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        lineHeight: 1,
                      }}
                    >
                      {cartItemCount}
                    </Box>
                  )}

                  <Typography sx={labelStyle}>Cart</Typography>
                </Box>
              </Tooltip>
            </Box>
          )}
        </Toolbar>
      </Container>

      {/* Drawer (re-used for mobile hamburger) */}
      <Drawer
        anchor="top"
        open={mobileOpen}
        onClose={toggleDrawer}
        PaperProps={{
          sx: {
            width: '100%',
            backgroundColor: '#000',
            boxShadow: 3,
            pt: 1,
          },
        }}
      >
        <Box
          sx={{
            width: '100%',
            display: 'flex',
            flexDirection: 'column',
            gap: 1,
            pb: 2,
          }}
          role="presentation"
        >
          {/* Close */}
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', pr: 2 }}>
            <IconButton
              onClick={toggleDrawer}
              aria-label="close"
              sx={{ color: 'white' }}
            >
              <CloseIcon />
            </IconButton>
          </Box>

          {/* Quick Actions */}
          <Box sx={{ px: 2, pb: 1 }}>
            <Typography
              variant="overline"
              sx={{ color: 'rgba(255,255,255,0.8)', letterSpacing: 1 }}
            >
              Quick Actions
            </Typography>

            {/* <Box
              component={RouterLink}
              to="/"
              onClick={toggleDrawer}
              sx={{
                display: 'block',
                color: 'white',
                textDecoration: 'none',
                py: 1,
                fontSize: 16,
              }}
            >
              Store
            </Box> */}

            {isLoggedIn ? (
              <>
                <Box
                  component={RouterLink}
                  to="/dashboard"
                  onClick={toggleDrawer}
                  sx={{
                    display: 'block',
                    color: 'white',
                    textDecoration: 'none',
                    py: 1,
                    fontSize: 16,
                  }}
                >
                  Dashboard
                </Box>
                <Box
                  component={RouterLink}
                  to="/settings"
                  onClick={toggleDrawer}
                  sx={{
                    display: 'block',
                    color: 'white',
                    textDecoration: 'none',
                    py: 1,
                    fontSize: 16,
                  }}
                >
                  Settings
                </Box>
                <Box
                  onClick={() => {
                    toggleDrawer();
                    handleLogout();
                  }}
                  sx={{
                    display: 'block',
                    color: 'white',
                    textDecoration: 'none',
                    py: 1,
                    fontSize: 16,
                    cursor: 'pointer',
                  }}
                >
                  Logout
                </Box>
              </>
            ) : (
              <>
                <Box
                  component={RouterLink}
                  to="/register"
                  onClick={toggleDrawer}
                  sx={{
                    display: 'block',
                    color: 'white',
                    textDecoration: 'none',
                    py: 1,
                    fontSize: 16,
                  }}
                >
                  Sign Up
                </Box>
                <Box
                  component={RouterLink}
                  to="/login"
                  onClick={toggleDrawer}
                  sx={{
                    display: 'block',
                    color: 'white',
                    textDecoration: 'none',
                    py: 1,
                    fontSize: 16,
                  }}
                >
                  Login
                </Box>
              </>
            )}

            <Box
              component={RouterLink}
              to="/cart/1"
              onClick={toggleDrawer}
              sx={{
                display: 'block',
                color: 'white',
                textDecoration: 'none',
                py: 1,
                fontSize: 16,
              }}
            >
              Cart{cartItemCount ? ` (${cartItemCount})` : ''}
            </Box>
          </Box>

          {/* Admin */}
          {isLoggedIn && user?.role === 'admin' && (
            <>
              <Box sx={{ px: 2, pt: 1 }}>
                <Typography
                  variant="overline"
                  sx={{ color: 'rgba(255,255,255,0.8)', letterSpacing: 1 }}
                >
                  Admin
                </Typography>

                {[
                  ['Notifications', '/notifications'],
                  ['Discounts', '/discounts'],
                  ['Reseller Codes', '/reseller-codes'],
                  ['Transactions', '/transactions'],
                  ['Webhooks', '/webhooks'],
                  ['Users', '/users'],
                ].map(([label, to]) => (
                  <Box
                    key={label}
                    component={RouterLink}
                    to={to as string}
                    onClick={toggleDrawer}
                    sx={{
                      display: 'block',
                      color: 'white',
                      textDecoration: 'none',
                      py: 1,
                      fontSize: 16,
                    }}
                  >
                    {label}
                  </Box>
                ))}
              </Box>
              <Box
                sx={{
                  height: 1,
                  bgcolor: 'rgba(255,255,255,0.12)',
                  mx: 2,
                  my: 1,
                }}
              />
            </>
          )}

          {/* Categories */}
          {[
            { label: 'Video Plugins', to: '/' },
            { label: 'Subscriptions', to: '/store/subscriptions' },
            { label: 'Bundles', to: '/store/bundles' },
            { label: 'Upgrades', to: '/store/upgrades' },
            { label: 'Photo Plugins', to: '/store/photo-plugins' },
          ].map(({ label, to }) => {
            const isActive = location.pathname === to;
            return (
              <Box
                key={label}
                component={RouterLink}
                to={to}
                onClick={toggleDrawer}
                sx={{
                  fontWeight: 900,
                  textTransform: 'uppercase',
                  fontSize: '18px',
                  textAlign: 'center',
                  py: 1,
                  textDecoration: 'none',
                  backgroundColor: isActive ? '#968c8cff' : 'transparent',
                  color: 'white',
                  '&:hover': {
                    backgroundColor: isActive
                      ? '#968c8cff'
                      : 'rgba(255,255,255,0.1)',
                  },
                }}
              >
                {label}
              </Box>
            );
          })}
          <DownloadsNav variant="mobile" onNavigate={toggleDrawer} />
        </Box>
      </Drawer>

      {/* Desktop category bar (unchanged) */}
      {!isMobile && (
        <Box sx={{ backgroundColor: '#252525', py: 1 }}>
          <Container
            maxWidth="lg"
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              gap: 2.5,
              overflowX: 'hidden',
              whiteSpace: 'nowrap',
            }}
          >
            {[
              { label: 'Video Plugins', to: '/' },
              { label: 'Subscriptions', to: '/store/subscriptions' },
              { label: 'Bundles', to: '/store/bundles' },
              { label: 'Upgrades', to: '/store/upgrades' },
              { label: 'Photo Plugins', to: '/store/photo-plugins' },
            ].map(({ label, to }) => {
              const isActive = location.pathname === to;
              const style = {
                fontWeight: 900,
                textTransform: 'uppercase' as const,
                fontSize: '16px',
                px: 1,
                py: 0.5,
                borderRadius: '4px',
                textDecoration: 'none',
                display: 'inline-block',
                '&:hover': { textDecoration: 'underline' },
                color: isActive ? '#E53935' : 'white',
              };

              return (
                <Typography
                  key={label}
                  component={RouterLink}
                  to={to}
                  sx={style}
                >
                  {label}
                </Typography>
              );
            })}
            <DownloadsNav variant="desktop" />
          </Container>
        </Box>
      )}
    </AppBar>
  );
};

export default TopBar;
